import React from "react";
import { LineChart, Line, ResponsiveContainer } from "recharts";
import { Info, Database } from "lucide-react";
import usePulseLocale from "./i18n";
import { tokens, fmtCompact, fmtPct, fmtPeriodLong } from "./theme";
import { sourceLabel, datasetLabel } from "./datasetLabels";

// Citation rule: human-readable label in text; technical code only in tooltip.
export const SourceChip = ({ source, dataset, period, experimental, dm, testid }) => {
  const { t, lang } = usePulseLocale();
  const T = tokens(dm);
  const ds = dataset ? datasetLabel(dataset, lang) : null;
  return (
    <div className="flex items-center gap-1.5 flex-wrap" data-testid={testid || "source-chip"}>
      <span className={`text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5${dataset ? " cursor-help" : ""}`}
        title={dataset ? `${t("tech_ref")}: ${dataset}` : undefined}
        style={{ background: T.brandSoft, color: T.accent, borderRadius: 2 }}>
        {t("source")}: {sourceLabel(source)}{ds ? ` — ${ds}` : ""}
      </span>
      {period && (
        <span className="text-[10px] px-1.5 py-0.5" style={{ background: T.surface2, color: T.muted, borderRadius: 2 }}>
          {t("data_as_of", { period: fmtPeriodLong(period, lang) })}
        </span>
      )}
      {experimental && <ExperimentalBadge dm={dm} />}
    </div>
  );
};

export const ExperimentalBadge = ({ dm }) => {
  const { t } = usePulseLocale();
  const T = tokens(dm);
  return (
    <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 cursor-help"
      style={{ background: T.expBg, color: T.expText, borderRadius: 2 }}
      title={t("experimental_note")} data-testid="experimental-badge">
      {t("experimental")}
    </span>
  );
};

export const DeltaBadge = ({ pct, dm, suffix }) => {
  const { t, lang } = usePulseLocale();
  const T = tokens(dm);
  if (pct === null || pct === undefined) return null;
  const up = pct >= 0;
  return (
    <span className="text-xs font-bold" style={{ color: up ? T.up : T.down }}>
      {up ? "▲" : "▼"} {fmtPct(Math.abs(pct), lang, false)} {suffix || t("yoy")}
    </span>
  );
};

/** Series-break marker (ISTAT 2025 perimeter): chip with the producer's note as tooltip + link to the source. */
export const breakNote = (brk, lang) => (lang === "it" ? brk?.note_it || brk?.note : brk?.note);

export const BreakBadge = ({ brk, dm, short = false, testid }) => {
  const { t, lang } = usePulseLocale();
  if (!brk) return null;
  const note = breakNote(brk, lang);
  return (
    <a href={brk.url} target="_blank" rel="noopener noreferrer" title={note}
      className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 align-middle"
      style={{ background: dm ? "#3a2e12" : "#fdf3d8", color: dm ? "#f3cf6a" : "#8a5a00", borderRadius: 2, textDecoration: "none" }}
      data-testid={testid || "break-badge"} aria-label={`${t("break_badge")} — ${note}`}>
      <span aria-hidden="true">⚠</span> {short ? t("break_badge_short") : t("break_badge")}
    </a>
  );
};

/** Producer caution note on an (area, indicator) pair — e.g. Türkiye 2023/2025 coverage; same look as the break badge,
 *  no link, tooltip = the note in the current language. */
export const NoteBadge = ({ note, dm, label, testid }) => {
  const { t, lang } = usePulseLocale();
  if (!note) return null;
  const text = lang === "it" ? note.note_it || note.note : note.note;
  return (
    <span title={text} tabIndex={0} role="note" aria-label={`${label || t("note_badge")} — ${text}`}
      className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 align-middle cursor-help"
      style={{ background: dm ? "#3a2e12" : "#fdf3d8", color: dm ? "#f3cf6a" : "#8a5a00", borderRadius: 2 }}
      data-testid={testid || "note-badge"}>
      <span aria-hidden="true">⚠</span><span>{label || t("note_badge")}</span>
    </span>
  );
};

export const Spark = ({ data, dm, height = 34 }) => {
  const T = tokens(dm);
  if (!data || data.length < 2) return <div style={{ height }} />;
  return (
    <div style={{ height }} aria-hidden="true">
      <ResponsiveContainer width="100%" height={height}>
        <LineChart data={data} margin={{ top: 2, right: 2, bottom: 2, left: 2 }}>
          <Line type="monotone" dataKey="value" stroke={T.accent} strokeWidth={1.5}
            dot={false} isAnimationActive={false} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

export const KpiCard = ({ label, tip, value, unit, period, yoy, spark, source, dataset,
  badge, extra, dm, testid }) => {
  const { lang } = usePulseLocale();
  const T = tokens(dm);
  return (
    <div className="p-4 flex flex-col gap-2" data-testid={testid}
      style={{ background: T.surface, border: `1px solid ${T.border}` }}>
      <div className="flex items-start justify-between gap-2">
        <span className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: T.muted }}>
          {label}
          {tip && <Info className="inline w-3 h-3 ml-1 -mt-0.5 cursor-help" aria-label={tip} title={tip} />}
        </span>
        {badge}
      </div>
      <div className="flex items-baseline gap-2 flex-wrap">
        <span className="pulse-num text-3xl font-semibold" style={{ color: T.text }}>
          {typeof value === "number" ? fmtCompact(value, lang) : (value ?? "—")}
        </span>
        {unit && <span className="text-xs" style={{ color: T.muted }}>{unit}</span>}
        <DeltaBadge pct={yoy} dm={dm} />
      </div>
      {spark && <Spark data={spark} dm={dm} />}
      {extra}
      {source && <SourceChip source={source} dataset={dataset} period={period} dm={dm} />}
    </div>
  );
};

export const SkeletonCard = ({ h = 120, dm }) => {
  const T = tokens(dm);
  return (
    <div className="pulse-skel" data-testid="pulse-skeleton"
      style={{ height: h, background: T.surface, border: `1px solid ${T.border}` }} />
  );
};

export const NoData = ({ msg, dm, h = 120, testid }) => {
  const { t } = usePulseLocale();
  const T = tokens(dm);
  return (
    <div className="flex flex-col items-center justify-center gap-2 p-4 text-center"
      data-testid={testid || "pulse-no-data"}
      style={{ minHeight: h, background: T.surface, border: `1px dashed ${T.border}` }}>
      <Database className="w-5 h-5" style={{ color: T.muted }} aria-hidden="true" />
      <span className="text-xs" style={{ color: T.muted }}>{msg || t("no_data")}</span>
    </div>
  );
};

// Screen-reader text alternative for every chart (WCAG — brief §3.7)
export const SrTable = ({ caption, columns, rows }) => (
  <div className="sr-only">
    <table>
      <caption>{caption}</caption>
      <thead>
        <tr>{columns.map((c) => <th key={c} scope="col">{c}</th>)}</tr>
      </thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={i}>{r.map((cell, j) => <td key={j}>{cell}</td>)}</tr>
        ))}
      </tbody>
    </table>
  </div>
);

export const SectionTitle = ({ children, dm, right }) => {
  const T = tokens(dm);
  return (
    <div className="flex items-end justify-between gap-3 mb-3 flex-wrap">
      <h2 className="pulse-serif text-lg md:text-lg font-semibold" style={{ color: T.text }}>{children}</h2>
      {right}
    </div>
  );
};

export const ChartTooltipContent = ({ active, payload, label, dm, formatter }) => {
  const T = tokens(dm);
  if (!active || !payload || !payload.length) return null;
  return (
    <div className="px-3 py-2 text-xs" style={{
      background: dm ? "#0F1115" : "#111827", color: "#F3F4F6",
      border: `1px solid ${T.border}`, borderRadius: 2 }}>
      <div className="font-semibold mb-1">{label}</div>
      {payload.map((p, i) => (
        <div key={i} className="flex items-center gap-2">
          <span style={{ width: 8, height: 8, background: p.color, display: "inline-block" }} />
          <span>{p.name}:</span>
          <span className="pulse-num font-semibold">{formatter ? formatter(p.value) : p.value}</span>
        </div>
      ))}
    </div>
  );
};
